import React, { useEffect, useState, useMemo } from "react";
import ContainerDashboard from "../../components/layouts/ContainerDashboard";
import { Link } from "react-router-dom";
import config, { axiosInstance } from "../Config";
import secureLocalStorage from "react-secure-storage";
import ReactApexChart from 'react-apexcharts';
import logo from '../../Assets/Images/bird.png'
import dImg1 from '../../Assets/Images/dashboard1.png'
import dImg2 from '../../Assets/Images/dashboard2.png'
import dImg3 from '../../Assets/Images/Dashboard3.png'
import dImg4 from '../../Assets/Images/Dashboard4.png'

const STAT_TEMPLATES = [
    {
        label: "AI Images",
        key: "totalGeneratedImages",
        desc: "Images created by kids",
        emoji: <img src={dImg1} />,
        icon: "bi bi-image-fill",
        gradient: "linear-gradient(135deg, #A44CD7 0%, #F55145 100%)",
        link: `${config.appUrl}ai-generated/images`,
    },
    {
        label: "AI Videos",
        key: "totalGeneratedVideos",
        desc: "Videos created by kids",
        emoji: <img src={dImg2} />,
        icon: "bi bi-play-circle-fill",
        gradient: "linear-gradient(135deg, #0CACC2 0%, #A44CD7 100%)",
        link: `${config.appUrl}ai-generated/videos`,
    },
    {
        label: "Our Videos",
        key: "totalVideos",
        desc: "Uploaded learning videos",
        emoji:<img src={dImg3} />,
        icon: "bi bi-film",
        gradient: "linear-gradient(135deg, #F9BE22 0%, #F55145 100%)",
        link: `${config.appUrl}videos`,
    },
    {
        label: "Staff Members",
        key: "totalStaff",
        desc: "Active team members",
        emoji: <img src={dImg4} />,
        icon: "icon-users2",
        gradient: "linear-gradient(135deg, #A44CD7 0%, #0CACC2 100%)",
        link: `${config.appUrl}staff`,
    },
];

const quickLinks = [
    { label: "AI Chats",            url: `${config.appUrl}ai-chats`,                  icon: "bi bi-chat-dots",    color: "#A44CD7", desc: "View AI conversations with kids" },
    { label: "Roles & Permissions", url: `${config.appUrl}roles`,                     icon: "icon-user-lock",     color: "#0CACC2", desc: "Define access levels for each role" },
    { label: "Privacy Policy",      url: `${config.appUrl}settings/privacy-policy`,   icon: "bi bi-shield-lock",  color: "#F55145", desc: "Manage app privacy policy content" },
    { label: "Category",            url: `${config.appUrl}settings/category`,         icon: "bi bi-grid",         color: "#A44CD7", desc: "Manage video categories for the app" },
    { label: "Restricted Words",    url: `${config.appUrl}settings/restricted-words`, icon: "bi bi-slash-circle", color: "#0CACC2", desc: "Manage the blocked words list" },
    { label: "Audit Logs",          url: `${config.appUrl}audit`,                     icon: "icon-files",         color: "#F55145", desc: "Track all system activity" },
];

const fallbackUsersCategories = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul']
const fallbackUsersSeries = [{ name: 'Users Registered', data: [28, 45, 38, 62, 71, 84, 93] }]
const fallbackTokensCategories = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul']
const fallbackTokensSeries = [{ name: 'Tokens Used', data: [12400, 18200, 15800, 24600, 31200, 28900, 42100] }]

const baseUsersChartOptions = {
    chart: { type: 'area', height: 280, toolbar: { show: false }, zoom: { enabled: false } },
    colors: ['#A44CD7'],
    fill: {
        type: 'gradient',
        gradient: { shadeIntensity: 1, opacityFrom: 0.40, opacityTo: 0.03, stops: [0, 100] },
    },
    stroke: { curve: 'smooth', width: 2.5 },
    dataLabels: { enabled: false },
    xaxis: {
        categories: fallbackUsersCategories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: '#999', fontSize: '12px' } },
    },
    yaxis: { labels: { style: { colors: '#999', fontSize: '12px' } } },
    grid: { borderColor: '#f0f0f0', strokeDashArray: 4 },
    tooltip: { theme: 'light' },
    markers: { size: 4, colors: ['#A44CD7'], strokeWidth: 0 },
}

const baseTokensChartOptions = {
    chart: { type: 'bar', height: 280, toolbar: { show: false }, zoom: { enabled: false } },
    colors: ['#0CACC2'],
    plotOptions: { bar: { borderRadius: 6, columnWidth: '52%', distributed: false } },
    dataLabels: { enabled: false },
    xaxis: {
        categories: fallbackTokensCategories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: '#999', fontSize: '12px' } },
    },
    yaxis: {
        labels: {
            style: { colors: '#999', fontSize: '12px' },
            formatter: v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v,
        },
    },
    grid: { borderColor: '#f0f0f0', strokeDashArray: 4 },
    tooltip: {
        theme: 'light',
        y: { formatter: v => `${v.toLocaleString()} tokens` },
    },
}

const Index = () => {
    const user = secureLocalStorage.getItem("admin_credentials");
    const [dashboardData, setDashboardData] = useState({});

    useEffect(() => {
        document.title = "Admin Dashboard | Mindora";
        window.scrollTo(0, 0);
        fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
        try {
            const res = await axiosInstance.get('dashboard');
            if (res.data?.success) setDashboardData(res.data.data);
        } catch (_) {}
    };

    const usersGraph = dashboardData?.usersGraph;
    const tokensGraph = dashboardData?.tokensGraph;
    const usersFallbackChart = dashboardData?.usersRegisteredChart;
    const tokensFallbackChart = dashboardData?.tokensUsedChart;

    const usersCategories = useMemo(() => {
        if (usersGraph?.categories?.length) return usersGraph.categories;
        if (usersFallbackChart?.categories?.length) return usersFallbackChart.categories;
        return fallbackUsersCategories;
    }, [usersGraph, usersFallbackChart]);

    const usersSeries = useMemo(() => {
        if (usersGraph?.series?.length) return usersGraph.series;
        if (usersGraph?.data?.length) return [{ name: 'Users Registered', data: usersGraph.data }];
        if (usersFallbackChart?.series?.length) return usersFallbackChart.series;
        return fallbackUsersSeries;
    }, [usersGraph, usersFallbackChart]);

    const tokensCategories = useMemo(() => {
        if (tokensGraph?.categories?.length) return tokensGraph.categories;
        if (tokensFallbackChart?.categories?.length) return tokensFallbackChart.categories;
        return fallbackTokensCategories;
    }, [tokensGraph, tokensFallbackChart]);

    const tokensSeries = useMemo(() => {
        if (tokensGraph?.series?.length) return tokensGraph.series;
        if (tokensGraph?.data?.length) return [{ name: 'Tokens Used', data: tokensGraph.data }];
        if (tokensFallbackChart?.series?.length) return tokensFallbackChart.series;
        return fallbackTokensSeries;
    }, [tokensGraph, tokensFallbackChart]);

    const usersChartOptions = useMemo(() => ({
        ...baseUsersChartOptions,
        xaxis: { ...baseUsersChartOptions.xaxis, categories: usersCategories }
    }), [usersCategories]);

    const tokensChartOptions = useMemo(() => ({
        ...baseTokensChartOptions,
        xaxis: { ...baseTokensChartOptions.xaxis, categories: tokensCategories }
    }), [tokensCategories]);

    const totalUsersDisplay = dashboardData?.totalUsers ?? usersGraph?.total ?? usersFallbackChart?.total ?? null;
    const totalTokensDisplay = dashboardData?.totalTokensUsed ?? tokensGraph?.total ?? tokensFallbackChart?.total ?? null;

    const formatTokens = (n) => {
        if (n == null) return "—";
        if (n >= 1000) {
            const k = n / 1000;
            return (k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)) + "k";
        }
        return Number(n).toLocaleString();
    };

    const usersGrowthPercent = usersGraph?.growthPercent ?? usersFallbackChart?.growthPercent ?? null;
    const usersGrowthLabel = usersGraph?.growthLabel ?? (usersGrowthPercent != null ? `${usersGrowthPercent > 0 ? '+' : ''}${usersGrowthPercent}% vs last month` : null);
    const tokensGrowthPercent = tokensGraph?.growthPercent ?? tokensFallbackChart?.growthPercent ?? null;
    const tokensGrowthLabel = tokensGraph?.growthLabel ?? (tokensGrowthPercent != null ? `${tokensGrowthPercent > 0 ? '+' : ''}${tokensGrowthPercent}% vs last month` : null);

    return (
        <ContainerDashboard title="Dashboard" description="Welcome to Mindora Admin Panel">
            <section className="ps-dashboards p-4" id="homepages">

                {/* Welcome Banner */}
                <div
                    className="rounded-4 p-4 mb-4 position-relative overflow-hidden"
                    style={{ background: "linear-gradient(135deg, #A44CD7 0%, #0CACC2 60%, #F9BE22 100%)" }}
                >
                    <div style={{ position: 'absolute', top: -35, right: -35, width: 170, height: 170, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                    <div style={{ position: 'absolute', top: 15, right: 90, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <div style={{ position: 'absolute', bottom: -45, right: 210, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <div className="position-relative d-flex align-items-center gap-3">
                        <span style={{  }}><img height={60} src={logo} /></span>
                        <div>
                            <h4 className="mb-1 fw-bold" style={{ color: '#fff' }}>
                                Welcome back, {user?.name || "Admin"}!
                            </h4>
                            <p className="mb-0" style={{ color: 'rgba(255,255,255,0.82)', fontSize: '14px' }}>
                                Here's what's happening in Mindora today.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Stat Cards */}
                <div className="row g-3 mb-4">
                    {STAT_TEMPLATES.map((card, i) => (
                        <div className="col-xl-3 col-md-6" key={i}>
                            <Link to={card.link} style={{ textDecoration: 'none' }}>
                                <div
                                    className="rounded-4 p-4 d-flex align-items-center position-relative overflow-hidden"
                                    style={{ background: card.gradient, minHeight: 155, boxShadow: '0 4px 20px rgba(0,0,0,0.13)', transition: 'transform 0.22s, box-shadow 0.22s', cursor: 'pointer' }}
                                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 10px 32px rgba(0,0,0,0.20)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.13)'; }}
                                >
                                    <i className={card.icon} style={{ position: 'absolute', bottom: -12, right: -8, fontSize: 95, color: 'rgba(255,255,255,0.13)', lineHeight: 1, pointerEvents: 'none' }} />
                                    <div className="d-flex align-items-center w-100">
                                        <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0" style={{ width: 90, height: 90, fontSize: 30 }}>
                                            {card.emoji}
                                        </div>
                                        <div className="flex-grow-1 text-center" style={{ minWidth: 0 }}>
                                            <span className="d-inline-block mb-1 rounded-3 px-3 py-1" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}>
                                                <span className="fw-bold" style={{ color: '#fff', fontSize: '2.6rem', lineHeight: 1, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>{dashboardData[card.key] ?? "—"}</span>
                                            </span>
                                            <p className="fw-semibold mb-0" style={{ color: '#fff', fontSize: '14px' }}>{card.label}</p>
                                            <p className="mb-0" style={{ color: 'rgba(255,255,255,0.72)', fontSize: '12px' }}>{card.desc}</p>
                                            <div className="mt-2 d-flex align-items-center justify-content-center gap-1" style={{ color: 'rgba(255,255,255,0.88)', fontSize: '12px', fontWeight: 600 }}>
                                                View all <i className="bi bi-arrow-right-short" style={{ fontSize: 16 }}></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>

                {/* Charts */}
                <div className="row g-3 mb-4">
                    {/* Users Registered */}
                    <div className="col-xl-6">
                        <div className="rounded-4 p-4" style={{ background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <div>
                                    <h6 className="fw-bold mb-0" style={{ color: '#1a1a2e', fontSize: 15 }}>Users Registered</h6>
                                    <p className="mb-0" style={{ fontSize: 12, color: '#999' }}>Monthly new user sign-ups</p>
                                </div>
                                <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 40, height: 40, background: 'rgba(164,76,215,0.10)' }}>
                                    <i className="bi bi-people-fill" style={{ fontSize: 18, color: '#A44CD7' }}></i>
                                </div>
                            </div>
                            <div className="d-flex align-items-baseline gap-2 mb-3">
                                <h3 className="fw-bold mb-0" style={{ color: '#1a1a2e' }}>{totalUsersDisplay != null ? Number(totalUsersDisplay).toLocaleString() : "—"}</h3>
                                {usersGrowthLabel && (
                                    <span style={{ fontSize: 12, color: usersGrowthPercent != null && usersGrowthPercent < 0 ? '#F55145' : '#2ecc71', fontWeight: 600 }}>
                                        <i className={`bi ${usersGrowthPercent != null && usersGrowthPercent < 0 ? 'bi-arrow-down-short' : 'bi-arrow-up-short'}`}></i>{usersGrowthLabel}
                                    </span>
                                )}
                            </div>
                            <ReactApexChart options={usersChartOptions} series={usersSeries} type="area" height={220} />
                        </div>
                    </div>

                    {/* Tokens Used */}
                    <div className="col-xl-6">
                        <div className="rounded-4 p-4" style={{ background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <div>
                                    <h6 className="fw-bold mb-0" style={{ color: '#1a1a2e', fontSize: 15 }}>Tokens Used</h6>
                                    <p className="mb-0" style={{ fontSize: 12, color: '#999' }}>Monthly AI token consumption</p>
                                </div>
                                <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 40, height: 40, background: 'rgba(12,172,194,0.10)' }}>
                                    <i className="bi bi-cpu-fill" style={{ fontSize: 18, color: '#0CACC2' }}></i>
                                </div>
                            </div>
                            <div className="d-flex align-items-baseline gap-2 mb-3">
                                <h3 className="fw-bold mb-0" style={{ color: '#1a1a2e' }}>{formatTokens(totalTokensDisplay)}</h3>
                                {tokensGrowthLabel && (
                                    <span style={{ fontSize: 12, color: tokensGrowthPercent != null && tokensGrowthPercent < 0 ? '#F55145' : '#2ecc71', fontWeight: 600 }}>
                                        <i className={`bi ${tokensGrowthPercent != null && tokensGrowthPercent < 0 ? 'bi-arrow-down-short' : 'bi-arrow-up-short'}`}></i>{tokensGrowthLabel}
                                    </span>
                                )}
                            </div>
                            <ReactApexChart options={tokensChartOptions} series={tokensSeries} type="bar" height={220} />
                        </div>
                    </div>
                </div>

                {/* Quick Access */}
                <h6 className="fw-bold mb-3" style={{ color: '#1a1a2e', fontSize: '15px', letterSpacing: '0.2px' }}>
                    Quick Access
                </h6>
                <div className="row g-3">
                    {quickLinks.map((item, i) => (
                        <div className="col-xl-4 col-md-6" key={i}>
                            <Link to={item.url} style={{ textDecoration: 'none' }}>
                                <div
                                    className="p-3 rounded-4 d-flex align-items-center gap-3"
                                    style={{ background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.05)', transition: 'transform 0.2s, box-shadow 0.2s' }}
                                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 22px rgba(0,0,0,0.10)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
                                >
                                    <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0" style={{ width: 44, height: 44, background: item.color + '1A' }}>
                                        <i className={item.icon} style={{ fontSize: 20, color: item.color }}></i>
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <p className="mb-0 fw-semibold" style={{ color: '#1a1a2e', fontSize: '14px' }}>{item.label}</p>
                                        <p className="mb-0" style={{ fontSize: '12px', color: '#888' }}>{item.desc}</p>
                                    </div>
                                    <i className="bi bi-chevron-right flex-shrink-0" style={{ color: '#ccc', fontSize: '14px' }}></i>
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>

            </section>
        </ContainerDashboard>
    );
};

export default Index;
